import React from 'react'
import ServicePage, { SplitSection, StackRibbon } from '../ServicePage'

const WebHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="w-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="w-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>

    <circle cx="230" cy="230" r="200" fill="url(#w-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* desktop browser */}
    <rect x="60" y="100" width="340" height="220" rx="14" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.18)" />
    <rect x="60" y="100" width="340" height="28" rx="14" fill="rgba(255,255,255,0.06)" />
    <circle cx="80" cy="114" r="4" fill="#ff6b6b" />
    <circle cx="94" cy="114" r="4" fill="#ffbb33" />
    <circle cx="108" cy="114" r="4" fill="#00c851" />
    <rect x="130" y="106" width="180" height="16" rx="8" fill="rgba(255,255,255,0.08)" />

    {/* hero image area inside browser */}
    <rect x="80" y="148" width="160" height="100" rx="8" fill="url(#w-grad)" opacity="0.85">
      <animate attributeName="opacity" values="0.7;1;0.7" dur="3.5s" repeatCount="indefinite" />
    </rect>
    <rect x="252" y="148" width="128" height="14" rx="3" fill="rgba(255,255,255,0.85)" />
    <rect x="252" y="170" width="128" height="10" rx="3" fill="rgba(255,255,255,0.40)" />
    <rect x="252" y="186" width="128" height="10" rx="3" fill="rgba(255,255,255,0.30)" />
    <rect x="252" y="220" width="80" height="22" rx="11" fill="url(#w-grad)" />

    <rect x="80" y="266" width="100" height="42" rx="8" fill="rgba(255,255,255,0.08)" />
    <rect x="190" y="266" width="100" height="42" rx="8" fill="rgba(255,255,255,0.08)" />
    <rect x="300" y="266" width="80"  height="42" rx="8" fill="rgba(255,255,255,0.08)" />

    {/* lighthouse score */}
    <g transform="translate(380 350)">
      <circle r="32" fill="rgba(0,200,81,0.10)" stroke="#00c851" />
      <text x="0" y="-2" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="18" fontWeight="700" fill="#00c851">100</text>
      <text x="0" y="14" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="8" fill="#00c851">PERF</text>
      <animateTransform attributeName="transform" type="translate" values="380 350;380 340;380 350" dur="5s" repeatCount="indefinite" />
    </g>
  </svg>
)

const WebDevelopment = () => (
  <ServicePage
    slug="web-development"
    seo={{
      title: 'Web Development Services',
      description:
        'Web development from QA Tech — fast, accessible, conversion-focused websites and web apps. SEO-ready, Core Web Vitals friendly and tested top-to-bottom.',
      keywords:
        'web development, web design, web applications, react development, next.js, headless CMS, web performance, Core Web Vitals, accessible web design, SEO web development, UK web developers',
    }}
    hero={{
      eyebrow: 'Service / Web Development',
      title: 'Websites that load fast and convert faster.',
      lede:
        'Modern, accessible websites and web apps engineered for performance, SEO and growth — and tested end-to-end so they stay that way.',
    }}
    visual={<WebHero />}
  >
    <SplitSection
      index={1}
      align="left"
      kicker="Sites & Web Apps"
      title="Marketing sites or full SaaS — same standard."
    >
      <p>
        From a punchy three-page site that wins enquiries, to a complex web
        application with auth, billing and integrations &mdash; we apply the
        same engineering rigour: clean component libraries, type safety,
        accessibility and proper testing.
      </p>
      <ul>
        <li>Marketing sites &amp; landing pages</li>
        <li>eCommerce storefronts (headless / Shopify / custom)</li>
        <li>Customer portals &amp; dashboards</li>
        <li>SaaS web applications</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={2}
      align="right"
      kicker="Performance"
      title="Core Web Vitals are a feature, not an afterthought."
      accent={
        <span className="sp-accent-stat">
          <span className="sp-accent-stat__num">90+</span>
          <span className="sp-accent-stat__label">Lighthouse scores</span>
        </span>
      }
    >
      <p>
        Image optimisation, code-splitting, edge caching, font subsetting,
        critical CSS, prefetching &mdash; and a budget that fails the build
        if it&rsquo;s busted. Your bounce rate cares. So does Google.
      </p>
    </SplitSection>

    <SplitSection
      index={3}
      align="left"
      kicker="SEO"
      title="Built to rank — not retrofitted."
      accent={<span className="sp-accent-code">{'/* schema.org first */'}</span>}
    >
      <p>
        Semantic HTML, structured data, clean URLs, fast TTFB, SSR/ISR where
        it matters, automatic sitemaps and Open Graph, and analytics wired up
        from launch. SEO isn&rsquo;t a plugin we install &mdash; it&rsquo;s
        baked into the architecture.
      </p>
      <ul>
        <li>Server-side rendering &amp; static generation</li>
        <li>Schema.org / JSON-LD on every page</li>
        <li>OpenGraph, Twitter cards, canonical tags</li>
        <li>Sitemap, robots, hreflang &amp; redirects</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={4}
      align="right"
      kicker="Accessibility"
      title="WCAG 2.2 AA — no excuses."
    >
      <p>
        Every site we ship hits WCAG 2.2 AA. We test with screen readers, not
        just lint rules. The result: a site that&rsquo;s genuinely usable for
        more customers &mdash; and won&rsquo;t land you on the wrong side of
        an accessibility audit.
      </p>
    </SplitSection>

    <SplitSection
      index={5}
      align="left"
      kicker="CMS"
      title="Editors who actually enjoy the CMS."
    >
      <p>
        Headless CMS (Sanity, Contentful, Strapi, Storyblok) or Webflow /
        WordPress where it makes sense. We design the editing experience as
        carefully as the front end, so your team can update the site without
        opening a Jira ticket.
      </p>
    </SplitSection>

    <SplitSection
      index={6}
      align="right"
      kicker="Quality"
      title="The whole site is tested. Yes, really."
    >
      <p>
        Visual regression on every PR, end-to-end checks on critical journeys,
        broken-link crawls, accessibility scans and performance budgets in CI.
        Releases stop being a leap of faith.
      </p>
    </SplitSection>

    <StackRibbon
      label="Stacks we work with"
      items={[
        'React', 'Next.js', 'Remix', 'Astro',
        'TypeScript', 'Tailwind CSS', 'Sass',
        'Sanity', 'Contentful', 'Strapi', 'Storyblok',
        'Shopify', 'WordPress', 'Webflow',
        'Vercel', 'Netlify', 'Cloudflare', 'AWS',
      ]}
    />
  </ServicePage>
)

export default WebDevelopment
